import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Instagram, Zap, MessageSquare, Link2, Sparkles,
  X, Check, ChevronRight, BarChart3,
} from "lucide-react";
import { Button } from "../ui/button";

/**
 * MobileQuickTour — bottom-sheet welcome + optional multi-step tour.
 *
 * On mobile we keep the original tour experience (users benefit from a
 * guided walkthrough on phones where the nav is collapsed). This file
 * contains the welcome sheet + the 6-step sequential walkthrough.
 *
 * Usage:
 *   <MobileQuickTour onComplete={...} onSkip={...} />
 */

const TOUR_STEPS = [
  { icon: Sparkles, title: "Your command center", description: "Track followers, DMs, conversions, and smart insights — all in one place." },
  { icon: Zap, title: "Automation flows", description: "Reply to comments & stories automatically. Turn every comment into a DM conversation." },
  { icon: Link2, title: "Link-in-Bio", description: "Create a branded creator page to convert every IG click into leads and sales." },
  { icon: MessageSquare, title: "Unified inbox", description: "All your Instagram DMs in one view. Reply faster with AI-suggested responses." },
  { icon: BarChart3, title: "Smart analytics", description: "Follower growth, engagement, top posts and AI insights — all updated in real-time." },
  { icon: Instagram, title: "Connect Instagram", description: "Link your Business account to unlock automations, inbox, and analytics." },
];

function ScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return null;
}

export default function MobileQuickTour({ onComplete, onSkip }) {
  // Two-phase flow: welcome → (optional) multi-step tour
  const [phase, setPhase] = useState("welcome"); // "welcome" | "tour"
  const [stepIndex, setStepIndex] = useState(0);

  const startTour = () => {
    setStepIndex(0);
    setPhase("tour");
  };
  const isLast = stepIndex === TOUR_STEPS.length - 1;

  return (
    <AnimatePresence mode="wait">
      {phase === "welcome" ? (
        <motion.div
          key="welcome"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm"
          onClick={onSkip}
          data-testid="mobile-quick-tour-welcome-backdrop"
        >
          <ScrollLock />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl overflow-hidden shadow-2xl max-h-[92dvh] flex flex-col pb-[env(safe-area-inset-bottom,0px)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-quick-tour-welcome"
          >
            {/* Drag handle */}
            <div className="w-10 h-1 rounded-full bg-border mx-auto mt-2 shrink-0" />

            {/* Hero with logo */}
            <div className="h-24 bg-gradient-to-br from-primary/20 via-violet-500/20 to-pink-500/10 flex items-center justify-center relative shrink-0">
              <button
                onClick={onSkip}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background/70 hover:bg-background/90 text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors active:scale-[0.94]"
                aria-label="Close"
                data-testid="mobile-quick-tour-welcome-close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-xl">
                <img src="/tadlo-logo.jpeg" alt="Tadlo" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Body */}
            <div className="p-5 text-center overflow-y-auto">
              <h2 className="text-xl font-semibold tracking-tight mb-1.5" style={{ fontFamily: "Outfit" }}>
                Welcome to Tadlo!
              </h2>
              <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                The AI-powered platform to automate Instagram and grow your business on autopilot.
              </p>
              <div className="space-y-2.5 mb-4">
                {[
                  { icon: Zap, text: "Automate DMs from comments & stories" },
                  { icon: Link2, text: "Create link-in-bio pages that convert" },
                  { icon: BarChart3, text: "AI analytics & growth insights" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-left p-2.5 rounded-xl bg-secondary/40">
                    <f.icon className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm">{f.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2.5">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={onSkip}
                  data-testid="mobile-quick-tour-skip"
                >
                  Skip
                </Button>
                <Button
                  className="flex-1"
                  onClick={startTour}
                  data-testid="mobile-quick-tour-start"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Quick Tour
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          key={`step-${stepIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
          data-testid="mobile-quick-tour-step-backdrop"
        >
          <ScrollLock />
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="absolute bottom-0 left-0 right-0 bg-background border-t border-border rounded-t-3xl shadow-2xl pb-[env(safe-area-inset-bottom,0px)]"
            data-testid="mobile-quick-tour-step"
          >
            <div className="w-10 h-1 rounded-full bg-border mx-auto mt-2" />
            <div className="p-5">
              {(() => {
                const step = TOUR_STEPS[stepIndex];
                const Icon = step.icon;
                return (
                  <>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-violet-500/20 shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-lg leading-tight" style={{ fontFamily: "Outfit" }}>
                          {step.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1.5">
                        {TOUR_STEPS.map((_, i) => (
                          <div
                            key={i}
                            className={`h-1.5 rounded-full transition-all ${i === stepIndex ? "w-6 bg-primary" : "w-1.5 bg-secondary"}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" onClick={onSkip} data-testid="mobile-quick-tour-step-skip">Skip</Button>
                        {stepIndex > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStepIndex((i) => Math.max(i - 1, 0))}
                            data-testid="mobile-quick-tour-step-back"
                          >
                            Back
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={isLast ? onComplete : () => setStepIndex((i) => i + 1)}
                          data-testid={isLast ? "mobile-quick-tour-finish" : "mobile-quick-tour-next"}
                        >
                          {isLast ? (
                            <>
                              <Check className="w-4 h-4 mr-1" /> Done
                            </>
                          ) : (
                            <>
                              Next <ChevronRight className="w-4 h-4 ml-1" />
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
